<template>
  <div class="nf-timepicker"
    :class="{ open, disabled: isDisabled, 'nf-timepicker--form': formSkin }" ref="root">
    <div class="nf-timepicker-field" :class="{ open }">
      <input class="nf-timepicker-input" type="text" maxlength="8"
        ref="input" v-model="text" :placeholder="placeholderText" :disabled="isDisabled"
        @input="onType" @focus="onFocus" @blur="onBlur" @mouseup="onMouseUp"
        @keydown.enter.prevent="$event.target.blur()" />
      <span class="nf-timepicker-icon" @mousedown.prevent="toggle">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </span>
    </div>
    <!-- mousedown.prevent keeps the input focused while interacting with the
         popup, so consumers saving on blur don't fire early -->
    <div v-if="open" class="nf-timepicker-pop" @mousedown.prevent>
      <div v-for="opt in timeOptions" :key="opt.value" class="nf-timepicker-opt"
        :class="{ selected: opt.value === modelValue }" :data-value="opt.value"
        @click="onPick(opt.value)">
        {{ opt.label }}
      </div>
    </div>
  </div>
</template>

<script>
// Typeable time field, sibling of NfDatePicker. Native <input type="time"> has
// the same Chrome flaw as the native date input: while the picker popup is open
// (and it opens on a field click), typed digits never reach the input.
// Model is a 24-hour "HH:mm" string; display is 12-hour ("7:45 PM").
// Typing is parsed loosely: "745", "7:45", "0745p", "745 pm", "1945" all work.
//
// Works two ways, exactly like NfDatePicker:
//  - standalone: v-model="someHHmmString"
//  - as a Vue Formulate custom type: <FormulateInput type="nftime" .../>
export default {
  name: 'NfTimePicker',
  props: {
    value: { type: String, default: '' },
    placeholder: { type: String, default: 'hh:mm' },
    disabled: { type: Boolean, default: false },
    context: { type: Object, default: null },
  },
  data() {
    return { open: false, focused: false, text: '', selectOnUp: false }
  },
  created() {
    this.text = this.displayValue
  },
  computed: {
    modelValue() {
      return this.context ? this.context.model : this.value
    },
    isDisabled() {
      if (this.context) return !!this.context.attributes.disabled
      return this.disabled
    },
    placeholderText() {
      if (this.context && this.context.attributes.placeholder) {
        return this.context.attributes.placeholder
      }
      return this.placeholder
    },
    // Inside VF the field normally wears the app's generic input skin so it
    // matches its form siblings. Pass the `nf-chrome` attribute to keep the
    // component's own wizard-style chrome instead (rounded field, icon inside).
    formSkin() {
      if (!this.context) return false
      const attrs = this.context.attributes || {}
      return !('nf-chrome' in attrs || 'nfChrome' in attrs)
    },
    displayValue() {
      const m = String(this.modelValue || '').match(/^(\d{2}):(\d{2})$/)
      if (!m) return ''
      const h = Number(m[1])
      const h12 = h % 12 || 12
      return `${h12}:${m[2]} ${h < 12 ? 'AM' : 'PM'}`
    },
    // Half-hour grid for the popup, 12:00 AM .. 11:30 PM.
    timeOptions() {
      const out = []
      for (let h = 0; h < 24; h++) {
        for (const mm of ['00', '30']) {
          const h12 = h % 12 || 12
          out.push({
            value: `${String(h).padStart(2, '0')}:${mm}`,
            label: `${h12}:${mm} ${h < 12 ? 'AM' : 'PM'}`,
          })
        }
      }
      return out
    },
  },
  watch: {
    // Keep the text in sync with external value changes (quick-time selector,
    // prefill) — but not while the user is typing.
    modelValue() {
      if (!this.focused) this.text = this.displayValue
    },
    open(isOpen) {
      if (isOpen) {
        document.addEventListener('mousedown', this.onOutside)
        this.$nextTick(this.scrollToCurrent)
      } else {
        document.removeEventListener('mousedown', this.onOutside)
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutside)
  },
  methods: {
    commit(hhmm) {
      if (this.context) {
        this.context.model = hhmm
      } else {
        this.$emit('input', hhmm)
      }
    },
    toggle() {
      if (this.isDisabled) return
      this.open = !this.open
    },
    onFocus(e) {
      this.focused = true
      this.open = true
      // Select the whole value so typing over a prefilled field replaces it.
      // Without this, maxlength blocks every keystroke once the field is full
      // ("it's not typeable").
      this.selectOnUp = true
      e.target.select()
    },
    onMouseUp(e) {
      // The click's own mouseup would collapse the focus-select to a caret;
      // suppress it exactly once so the selection survives.
      if (this.selectOnUp) {
        e.preventDefault()
        this.selectOnUp = false
      }
    },
    onBlur() {
      this.focused = false
      this.selectOnUp = false
      // Focus truly left (popup clicks prevent blur), so the popup can close.
      this.open = false
      const hhmm = this.parseTime(this.text)
      if (hhmm) {
        if (hhmm !== this.modelValue) this.commit(hhmm)
        // Don't wait for the prop round-trip; show the canonical form now.
        this.text = this.formatDisplay(hhmm)
      } else {
        this.text = this.displayValue
      }
      if (this.context && typeof this.context.blurHandler === 'function') {
        this.context.blurHandler()
      }
    },
    onType() {
      const raw = String(this.text || '')
      // Backspacing the trailing "M" of " AM"/" PM" means the user is removing
      // the meridiem — don't resurrect it from the leftover letter.
      const merDeleted = / [APap]$/.test(raw)
      const digits = raw.replace(/\D/g, '').slice(0, 4)
      const merMatch = raw.match(/[APap]/)
      const mer = !merDeleted && merMatch ? merMatch[0].toUpperCase() : ''
      this.text = this.maskTime(digits, mer)
      if (!digits) {
        // Clear the model only when the field was actually emptied — a lone
        // meridiem letter is half-typed input, not a clear.
        if (raw.trim() === '') this.commit('')
        return
      }
      const hhmm = this.parseTime(this.text)
      if (hhmm) this.commit(hhmm)
    },
    // Cleave-style time mask, the sibling of the date field's: consume the
    // digit run left-to-right, insert the colon automatically, pad an
    // impossible minute digit ("9" then "7" -> "9:07"), and expand a typed
    // a/p into " AM"/" PM" — so typing looks like the native segments did.
    maskTime(digits, mer) {
      let hh = ''
      let idx = 0
      if (digits.length >= 2 && Number(digits.slice(0, 2)) <= 23) {
        hh = digits.slice(0, 2)
        idx = 2
      } else if (digits.length) {
        hh = digits[0]
        idx = 1
      }
      let mm = digits.slice(idx, idx + 2)
      if (mm.length === 1 && mm > '5') mm = '0' + mm
      let out = hh
      if (mm) out += ':' + mm
      if (out && mer) out += ` ${mer}M`
      return out
    },
    // Loose parse to a 24-hour "HH:mm" string; null when not (yet) a time.
    // A meridiem letter needs a 1-12 hour; without one, 0-23 is taken as 24h.
    parseTime(str) {
      const s = String(str || '').trim().toLowerCase().replace(/\s+/g, '')
      const m = s.match(/^(\d{1,2}):?(\d{2})?(a|p)?m?$/)
      if (!m) return null
      let h = Number(m[1])
      const min = m[2] === undefined ? 0 : Number(m[2])
      const mer = m[3]
      if (min > 59) return null
      if (mer) {
        if (h < 1 || h > 12) return null
        if (mer === 'a') h = h === 12 ? 0 : h
        else h = h === 12 ? 12 : h + 12
      } else if (h > 23) {
        return null
      }
      return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
    },
    formatDisplay(hhmm) {
      const [h, mm] = hhmm.split(':')
      const hn = Number(h)
      return `${hn % 12 || 12}:${mm} ${hn < 12 ? 'AM' : 'PM'}`
    },
    onPick(hhmm) {
      this.commit(hhmm)
      // Sync the text now (the modelValue watcher skips updates while focused)
      // and blur so blur-driven consumers get exactly one event, after commit.
      this.text = this.formatDisplay(hhmm)
      this.open = false
      if (this.$refs.input && document.activeElement === this.$refs.input) {
        this.$refs.input.blur()
      } else if (this.context && typeof this.context.blurHandler === 'function') {
        this.context.blurHandler()
      }
    },
    scrollToCurrent() {
      const pop = this.$el.querySelector('.nf-timepicker-pop')
      if (!pop) return
      // Exact match, else the nearest half-hour row before the current value.
      let target = pop.querySelector('.nf-timepicker-opt.selected')
      if (!target && this.modelValue) {
        const rows = Array.from(pop.querySelectorAll('.nf-timepicker-opt'))
        target = rows.filter((r) => r.dataset.value <= this.modelValue).pop()
      }
      if (target) pop.scrollTop = target.offsetTop - pop.clientHeight / 2 + 14
    },
    onOutside(e) {
      if (this.$refs.root && !this.$refs.root.contains(e.target)) {
        this.open = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.nf-timepicker {
  position: relative;
  width: 100%;
}

.nf-timepicker-field {
  height: 40px;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #d9dee2;
  border-radius: 8px;
  padding: 0 12px;
  background: #fff;
  box-sizing: border-box;

  &.open {
    border-color: #0b3a4c;
  }
}

.nf-timepicker.disabled .nf-timepicker-field {
  background: #f5f7fa;
}

.nf-timepicker-input {
  flex: 1 1 auto;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 14px;
  color: #273b4a;
  padding: 0;

  &::placeholder {
    color: #9aa5b1;
  }

  &:disabled {
    color: #9aa5b1;
    cursor: not-allowed;
  }
}

.nf-timepicker-icon {
  flex: 0 0 auto;
  color: #687681;
  cursor: pointer;
  display: inline-flex;
}

// When the field keeps its OWN chrome (standalone, or `nf-chrome` inside VF),
// the app's global input rules must not restyle the inner input — they carry
// !important, so these resets must outgun them on specificity AND importance.
.nf-timepicker:not(.nf-timepicker--form) .nf-timepicker-field .nf-timepicker-input {
  border: none !important;
  border-radius: 0 !important;
  height: auto !important;
  min-height: 0 !important;
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
  font-family: 'Poppins', sans-serif !important;
  font-size: 14px !important;
  color: #273b4a !important;
}

// Same trick as NfDatePicker: inside a Vue Formulate form the app's global
// input styles reach the inner <input> anyway, so drop our chrome there and
// let the input wear the standard skin; the icon overlays the right edge.
.nf-timepicker--form {
  .nf-timepicker-field {
    border: none;
    border-radius: 0;
    height: auto;
    padding: 0;
    background: transparent;
    position: relative;
  }

  .nf-timepicker-input {
    padding-right: 36px !important;
  }

  .nf-timepicker-icon {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
  }
}

.nf-timepicker--form.disabled .nf-timepicker-field {
  background: transparent;
}

.nf-timepicker-pop {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 50;
  width: 100%;
  min-width: 130px;
  max-height: 216px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d9dee2;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(21, 47, 74, 0.12);
  padding: 4px;
}

.nf-timepicker-opt {
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  color: #273b4a;
  cursor: pointer;

  &:hover {
    background: #f1f5f8;
  }

  &.selected {
    background: #0b3a4c;
    color: #fff;
  }
}
</style>
